/**
 * The one confirm before a schedule is sent to the tablet (BA-85 S2b–S4o). With nothing
 * active it is the send confirm; with an action or a sent schedule active it is the
 * replace confirm and names what it replaces. BA-55's rules: Cancel is the focused
 * default, nothing is written before Confirm, and the confirm button has the tap guard.
 */

import React, { useLayoutEffect, useRef } from 'react';
import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import { CurrentAction, isAwaitingApproval } from '../types/currentAction';
import { SentSchedule, isSentScheduleActive } from '../types/sentSchedule';
import { stepPosition } from '../utils/sentSchedule';
import { useTapGuard } from '../hooks/useTapGuard';
import { RichText } from './RichText';

/** What the dialog says, from the live state while it is open and not sending */
export type SendConfirmMode = 'send' | 'action' | 'actionAwaiting' | 'schedule' | 'same' | 'nothingNow';

export function sendConfirmMode(
  scheduleId: string,
  sentSchedule: SentSchedule | null,
  currentAction: CurrentAction | null,
  wasReplace: boolean,
): SendConfirmMode {
  if (sentSchedule && isSentScheduleActive(sentSchedule)) {
    return sentSchedule.scheduleId === scheduleId ? 'same' : 'schedule';
  }
  if (currentAction) {
    return isAwaitingApproval(currentAction) ? 'actionAwaiting' : 'action';
  }
  // A replace whose old thing ended while the dialog was open keeps its title and button
  return wasReplace ? 'nothingNow' : 'send';
}

interface SendScheduleConfirmDialogProps {
  open: boolean;
  scheduleId: string;
  name: string;
  deviceName: string;
  sentSchedule: SentSchedule | null;
  currentAction: CurrentAction | null;
  /** The current action's name, when there is one */
  actionName: string;
  tabletOffline: boolean;
  sending: boolean;
  onConfirm: () => void;
  onCancel: () => void;
}

export const SendScheduleConfirmDialog: React.FC<SendScheduleConfirmDialogProps> = ({
  open,
  scheduleId,
  name,
  deviceName,
  sentSchedule: liveSentSchedule,
  currentAction,
  actionName: liveActionName,
  tabletOffline,
  sending,
  onConfirm,
  onCancel,
}) => {
  const { t } = useTranslation();
  const theme = useTheme();
  const isPhone = useMediaQuery(theme.breakpoints.down('sm'));
  const guard = useTapGuard();
  const cancelRef = useRef<HTMLButtonElement>(null);
  const wasReplace = useRef(false);

  // What the dialog says follows the live state while it waits for Confirm, and is frozen while
  // it sends and fades out (BA-108): the backend writes the new sentSchedule before it answers,
  // which must not turn the dialog into a confirm to replace the schedule just sent
  const liveShown = {
    mode: sendConfirmMode(scheduleId, liveSentSchedule, currentAction, open && wasReplace.current),
    sentSchedule: liveSentSchedule,
    actionName: liveActionName,
  };
  const shown = useRef(liveShown);
  if (open && !sending) shown.current = liveShown;
  const { mode, sentSchedule, actionName } = shown.current;
  // The button keeps "Sending…" until the dialog has unmounted, not only while the POST runs (BA-120)
  const sendingLabel = useRef(false);
  if (sending) sendingLabel.current = true;
  else if (open) sendingLabel.current = false;
  const replace = mode !== 'send';

  // The guard starts when the dialog opens, and again when it turns into a replace confirm
  useLayoutEffect(() => {
    if (!open) {
      wasReplace.current = false;
      return;
    }
    guard.arm();
  }, [open, guard]);

  useLayoutEffect(() => {
    if (open && replace && !wasReplace.current) {
      wasReplace.current = true;
      guard.arm();
    }
  }, [open, replace, guard]);

  const close = () => {
    if (!sending) onCancel();
  };
  const handleDialogClose = (_event: object, reason: 'backdropClick' | 'escapeKeyDown') => {
    if (reason === 'backdropClick' && guard.guarding()) return;
    close();
  };

  const handleClick = (event: React.MouseEvent) => {
    if (guard.accept(event) && !sending) onConfirm();
  };

  const position = sentSchedule ? stepPosition(sentSchedule) : { n: 1, total: 0 };
  const old = mode === 'schedule' || mode === 'same' ? sentSchedule?.schedule?.name ?? '' : actionName;
  const values = { name, device: deviceName, old, n: position.n, total: position.total };

  const lines: string[] = (() => {
    switch (mode) {
      case 'send':
        return [tabletOffline ? 'sentSchedule.send.bodyOffline' : 'sentSchedule.send.body'];
      case 'action':
      case 'actionAwaiting':
        return [
          mode === 'action' ? 'sentSchedule.replace.action' : 'sentSchedule.replace.actionAwaiting',
          tabletOffline ? 'sentSchedule.replace.actionThenOffline' : 'sentSchedule.replace.actionThen',
        ];
      case 'schedule':
        return ['sentSchedule.replace.schedule',
          tabletOffline ? 'sentSchedule.replace.thenOffline' : 'sentSchedule.replace.then'];
      case 'same':
        return tabletOffline ? ['sentSchedule.replace.same', 'sentSchedule.replace.sameOffline']
          : ['sentSchedule.replace.same'];
      case 'nothingNow':
        return [tabletOffline ? 'sentSchedule.replace.nothingNowOffline' : 'sentSchedule.replace.nothingNow'];
    }
  })();

  return (
    <Dialog
      open={open}
      onClose={handleDialogClose}
      maxWidth="xs"
      fullWidth
      aria-labelledby="send-schedule-title"
      // autoFocus alone loses to the dialog's focus trap in a browser (BA-55)
      slotProps={{ transition: { onEntering: () => cancelRef.current?.focus() } }}
    >
      <DialogTitle id="send-schedule-title">
        {t(replace ? 'sentSchedule.replace.title' : 'sentSchedule.send.title')}
      </DialogTitle>
      <DialogContent>
        {lines.map((key, i) => (
          <DialogContentText key={key} sx={{ mt: i > 0 ? 1.5 : 0 }}>
            <RichText i18nKey={key} values={values} />
          </DialogContentText>
        ))}
      </DialogContent>
      <DialogActions
        disableSpacing
        sx={isPhone
          ? { flexDirection: 'column', alignItems: 'stretch', gap: '12px', p: '8px 24px 24px' }
          : { gap: 1, px: 3, pb: 2 }}
      >
        <Button
          variant="outlined"
          color={replace ? 'error' : 'primary'}
          disabled={sending}
          onPointerDown={guard.onPointerDown}
          onClick={handleClick}
          startIcon={sending ? <CircularProgress size={16} color="inherit" /> : undefined}
          sx={{
            textTransform: 'none',
            minWidth: 0,
            ...(isPhone
              ? { minHeight: 48, alignSelf: 'flex-start', maxWidth: 'calc(100% - 32px)' }
              : { minHeight: 40 }),
          }}
        >
          <Box component="span" sx={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
            {sending || sendingLabel.current
              ? t('schedules.sending')
              : t(replace ? 'sentSchedule.replace.confirm' : 'sentSchedule.send.confirm')}
          </Box>
        </Button>
        <Button
          ref={cancelRef}
          variant="contained"
          autoFocus
          disabled={sending}
          onClick={close}
          sx={{ textTransform: 'none', ...(isPhone ? { minHeight: 48 } : { minHeight: 40, minWidth: 96 }) }}
        >
          {t('common.cancel')}
        </Button>
      </DialogActions>
    </Dialog>
  );
};
